/*
  Вкладка «Рассрочка» настроек организации - решение владельца 13.10.2026.

  Конституция (§3.8) отдаёт границы рассрочки вам: самый долгий срок,
  наименьший взнос, начисляются ли пени. Агент в разговоре и страница должника
  предлагают рассрочку только в этих рамках, а пока рассрочка не
  предоставляется, о ней не говорят вовсе. До этого дня рамки можно было задать
  только скриптом - кабинет их не показывал.

  Раскладка выбрана владельцем по макетам: поля - как в варианте А (переключатель,
  три числа в строку, пени), справа «Расчёт платежей» из варианта Б.

  🔴 Срок больше срока давности - предупреждение, а не запрет (указание
  владельца). Текст предупреждения приходит с сервера вместе с расчётом, пока
  человек вводит, - кабинет не держит своей копии.

  Вкладка грузит условия сама, как ключи обмена: настройкам организации они
  нужны на одной вкладке из восьми.
*/

import { useEffect, useState } from 'react'

import { api, fieldErrors } from '../api/client.js'
import { parseCount } from '../api/parseCount.js'
import { LockedForm } from './AccessContext.jsx'
import { Field, Loading, Notice, Switch } from './Common.jsx'
import { asInput } from './entry/money.js'
import InstalmentPreview from './InstalmentPreview.jsx'
import useInstalmentPreview from './useInstalmentPreview.js'

const OFFERED = [
  { value: true, title: 'Предоставляем' },
  { value: false, title: 'Не предоставляем' },
]

const PENALTY = [
  { value: false, title: 'Не начисляются' },
  { value: true, title: 'Начисляются' },
]

/** Условия с сервера - в значения полей формы: пустое значение - пустая строка. */
function formOf(terms) {
  return {
    offered: terms.offered,
    max_months: terms.max_months ?? '',
    min_payment: asInput(terms.min_payment),
    first_payment_within_days: terms.first_payment_within_days ?? '',
    penalty_during: terms.penalty_during,
  }
}

const WHOLE_FIELDS = ['max_months', 'first_payment_within_days']

/** Причины, по которым целое поле не разобрать (опечатка), - «поле: причина». */
function wholeProblems(form) {
  const problems = {}
  for (const name of WHOLE_FIELDS) {
    const { error } = parseCount(form[name])
    if (error) problems[name] = error
  }
  return problems
}

/* Значения полей - в тело запроса: пустое поле уходит пустотой, а не нулём; опечатка не
   уходит вовсе (раньше `Number('6х')` превращалась в null, то есть «без ограничения»,
   аудит 26.10.2026, строка 299). */
function bodyOf(form) {
  return {
    offered: form.offered,
    max_months: parseCount(form.max_months).value,
    min_payment: form.min_payment === '' ? null : String(form.min_payment).replace(',', '.'),
    first_payment_within_days: parseCount(form.first_payment_within_days).value,
    penalty_during: form.penalty_during,
  }
}

export default function SettingsInstalment() {
  const [terms, setTerms] = useState(null)
  const [form, setForm] = useState(null)
  const [debt, setDebt] = useState('')
  const [loadError, setLoadError] = useState(null)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api
      .instalmentTerms()
      .then((answer) => {
        setTerms(answer)
        setForm(formOf(answer))
        setDebt(answer.example_debt ?? '')
      })
      .catch(setLoadError)
  }, [])

  const typos = form ? wholeProblems(form) : {}
  const hasTypos = Object.keys(typos).length > 0
  // Расчёт по опечатке не запрашивается: он посчитал бы «без ограничения».
  const body =
    form && !hasTypos ? { ...bodyOf(form), debt: debt === '' ? null : debt.replace(',', '.') } : null
  const { preview, pending, error: previewError } = useInstalmentPreview(body)

  if (loadError) return <Notice error={loadError} />
  if (!form) return <Loading what="Читаем условия рассрочки" />

  const set = (key, value) => {
    setSaved(false)
    setForm((current) => ({ ...current, [key]: value }))
  }
  const problems = { ...fieldErrors(error ?? previewError), ...typos }

  async function save(event) {
    event.preventDefault()
    if (hasTypos) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const answer = await api.saveInstalmentTerms(bodyOf(form))
      setTerms(answer)
      setForm(formOf(answer))
      setSaved(true)
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="instalment">
      <section className="card">
        <LockedForm onSubmit={save}>
          <p className="muted">
            Условия, в которых агент и страница должника предлагают рассрочку. Пока рассрочка не
            предоставляется, о ней не говорят вовсе.
          </p>

          <Field label="Рассрочка">
            <Switch value={form.offered} onChange={(value) => set('offered', value)} options={OFFERED} />
          </Field>

          {form.offered ? (
            <>
              <fieldset className="group">
                <legend className="group__title">Границы</legend>
                <div className="prices">
                  <Field label="Самый долгий срок, мес." error={problems.max_months}>
                    <input
                      className="field__input"
                      inputMode="numeric"
                      value={form.max_months}
                      onChange={(event) => set('max_months', event.target.value)}
                    />
                  </Field>
                  <Field
                    label="Наименьший взнос, ₽"
                    hint="Если при выбранном сроке взнос выйдет меньше, срок сократится сам. Пусто - наименьшего взноса нет."
                    error={problems.min_payment}
                  >
                    <input
                      className="field__input"
                      inputMode="decimal"
                      value={form.min_payment}
                      onChange={(event) => set('min_payment', event.target.value)}
                    />
                  </Field>
                  <Field
                    label="Первый взнос, дней"
                    hint="Не позже скольких дней после оформления должен прийти первый взнос. Он признаёт долг и прерывает давность: чем раньше, тем надёжнее."
                    error={problems.first_payment_within_days}
                  >
                    <input
                      className="field__input"
                      inputMode="numeric"
                      value={form.first_payment_within_days}
                      onChange={(event) => set('first_payment_within_days', event.target.value)}
                    />
                  </Field>
                </div>
                {(preview?.warnings ?? []).map((text) => (
                  <Notice kind="warn" key={text}>
                    {text}
                  </Notice>
                ))}
              </fieldset>

              <fieldset className="group">
                <legend className="group__title">Пени на время рассрочки</legend>
                <p className="muted">Должник узнаёт это до оформления, а не на третьем взносе.</p>
                <Switch
                  value={form.penalty_during}
                  onChange={(value) => set('penalty_during', value)}
                  options={PENALTY}
                />
              </fieldset>
            </>
          ) : null}

          {error ? <Notice error={error} /> : null}
          {saved ? <Notice kind="ok">Условия рассрочки сохранены.</Notice> : null}

          <button className="button" type="submit" disabled={busy || hasTypos}>
            {busy ? 'Сохраняем…' : 'Сохранить условия рассрочки'}
          </button>
        </LockedForm>
      </section>

      <InstalmentPreview
        offered={form.offered}
        debt={debt}
        onDebt={setDebt}
        exampleDebt={terms?.example_debt}
        preview={preview}
        pending={pending}
        error={previewError && !Object.keys(fieldErrors(previewError)).length ? previewError : null}
      />
    </div>
  )
}
